import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  useGetInventoryItemsRequest,
  useGetInventoryItemResolutionReasonsRequest,
  useGetInventoryByIdRequest,
  useWhoAmIQueryRequest
} from '../../hooks/useApi';

export const useManageDiscrepancies = () =>
{
  const {inventoryId} = useParams();
  const changeStoreParams = JSON.parse(localStorage.getItem('changeStoreParams'));
  const posId = changeStoreParams?.selectedPosId;

  const [{data, loading, error}, getInventoryItems] = useGetInventoryItemsRequest(posId);
  const [{data: reasonsData}, getReasons] = useGetInventoryItemResolutionReasonsRequest(posId);
  const [{data: inventoryData}, getInventoryById] = useGetInventoryByIdRequest(posId);
  const [{data: whoAmI}] = useWhoAmIQueryRequest();

  const [discrepancyItems, setDiscrepancyItems] = useState([]);
  const [originalItemsMap, setOriginalItemsMap] = useState({});
  

  useEffect(() =>
  {
    if (inventoryId && posId)
    {
      getInventoryItems({ bodyParams: {inventoryId: parseInt(inventoryId, 10)} });
      getReasons();
      getInventoryById({ queryParams: {inventoryId: parseInt(inventoryId, 10)} });
    }
  }, [inventoryId, posId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build originalItemsMap for API payload construction
  useEffect(() =>
  {
    if (Array.isArray(data))
    {
      const map = {};
      data.forEach(item => { map[item.itemId] = item; });
      setOriginalItemsMap(map);
    }
  }, [data]);

  // Initialize discrepancyItems from API data (only when data changes, i.e. initial load)
  useEffect(() =>
  {
    if (!Array.isArray(data)) return;
    setDiscrepancyItems(data.map(item => ({
      ...item,
      style: (item.modelCode && item.fabricCode) ? `${item.modelCode}${item.fabricCode}` : null,
    })));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const getReasonCodeForDifference = useCallback((difference) =>
  {
    if (difference > 0) return 'POSITIVE_INVENTORY_MOVEMENT';
    if (difference < 0) return 'NEGATIVE_INVENTORY_MOVEMENT';
    return null;
  }, []);

  const reasonsMap = useMemo(() =>
  {
    const map = {};
    if (Array.isArray(reasonsData))
    {
      reasonsData.forEach(r => { map[r.resolutionReasonId] = r.resolutionReasonDesc; });
    }
    return map;
  }, [reasonsData]);

  

  // Backfill resolutionReasonDesc when reasonsMap arrives
  useEffect(() =>
  {
    setDiscrepancyItems(prev => prev.map(item =>
    {
      if (!item.resolutionReason) return item;
      if (item.resolutionReasonDesc) return item;
      const desc = reasonsMap[item.resolutionReason];
      if (!desc) return item;
      return { ...item, resolutionReasonDesc: desc };
    }));
  }, [reasonsMap]);

  const isUnresolved = (status) => status !== 'RESOLVED' && status !== 'OK';

  const markItemResolved = useCallback((itemId) =>
  {
    setDiscrepancyItems(prev => prev.map(item =>
    {
      if (item.itemId !== itemId)
      {
        return item;
      }

      // Do not overwrite quantity-adjusted items
      if (item.resolutionReason === 'SCAN_QUANTITY_ADJUSTED')
      {
        return {
          ...item,
          status: 'RESOLVED',
          resolutionReason: 'SCAN_QUANTITY_ADJUSTED',
          resolutionReasonDesc: reasonsMap['SCAN_QUANTITY_ADJUSTED'] || item.resolutionReasonDesc || 'Scan Quantity Adjusted',
        };
      }

      const diff = (item.scannedQuantity ?? 0) - (item.expectedQuantity ?? 0);
      const autoReason = getReasonCodeForDifference(diff);

      return {
        ...item,
        status: 'RESOLVED',
        resolutionReason: autoReason,
        resolutionReasonDesc: autoReason ? reasonsMap[autoReason] || null : null,
      };
    }));
  }, [getReasonCodeForDifference, reasonsMap]);

  const resolveAllItems = useCallback(() =>
  {
    setDiscrepancyItems(prev => prev.map(item =>
    {
      // Resolve All should ONLY resolve pending items
      if (item.status !== 'PENDING')
      {
        return item;
      }

      const diff = (item.scannedQuantity ?? 0) - (item.expectedQuantity ?? 0);
      const autoReason = getReasonCodeForDifference(diff);

      if (!autoReason)
      {
        return item;
      }

      return {
        ...item,
        status: 'RESOLVED',
        resolutionReason: autoReason,
        resolutionReasonDesc: reasonsMap[autoReason] || null,
      };
    }));
  }, [getReasonCodeForDifference, reasonsMap]);

  const updateItemActual = useCallback((itemId, newActual) =>
  {
    setDiscrepancyItems(prev => prev.map(item =>
    {
      if (item.itemId !== itemId)
      {
        return item;
      }

      return {
        ...item,
        scannedQuantity: newActual,
        status: 'RESOLVED',
        resolutionReason: 'SCAN_QUANTITY_ADJUSTED',
        resolutionReasonDesc: reasonsMap['SCAN_QUANTITY_ADJUSTED'] || 'Scan Quantity Adjusted',
      };
    }));
  }, [reasonsMap]);

  // Build API payload items
  const getApiItems = useCallback(() =>
  {
    return discrepancyItems.map(item =>
    {
      const original = originalItemsMap[item.itemId] || {};
      const itemData = {
        inventoryId: parseInt(inventoryId, 10),
        posId: item.posId || original.posId || '',
        itemId: item.itemId || '',
        barcode: item.barcode || '',
        itemDesc: item.itemDesc || '',
        modelCode: item.modelCode || '',
        fabricCode: item.fabricCode || '',
        colorCode: item.colorCode || '',
        colorDesc: item.colorDesc || '',
        sizeDesc: item.sizeDesc || '',
        season: item.season || '',
        genderCode: item.genderCode || '',
        genderDesc: item.genderDesc || '',
        lineCode: item.lineCode || '',
        lineDesc: item.lineDesc || '',
        macroFamilyCode: item.macroFamilyCode || '',
        macroFamilyDesc: item.macroFamilyDesc || '',
        microFamilyCode: item.microFamilyCode || '',
        microFamilyDesc: item.microFamilyDesc || '',
        expectedQuantity: item.expectedQuantity ?? 0,
        scannedQuantity: item.scannedQuantity ?? 0,
        status: item.status || 'PENDING',
        createdOn: item.createdOn || new Date().toISOString(),
        createdBy: item.createdBy || '',
        updatedOn: new Date().toISOString(),
        updatedBy: item.updatedBy || '',
        resolutionReason: item.resolutionReason || getReasonCodeForDifference((item.scannedQuantity ?? 0) - (item.expectedQuantity ?? 0)),
      };
      return itemData;
    });
  }, [discrepancyItems, originalItemsMap, inventoryId]);

  const parsedInventory = inventoryData
    ? (typeof inventoryData === 'string' ? JSON.parse(inventoryData) : inventoryData)
    : null;

  const parsedWhoAmI = useMemo(() =>
  {
    if (!whoAmI) return null;
    if (typeof whoAmI === 'string')
    {
      try
      {
        return JSON.parse(whoAmI);
      } catch
      {
        return null;
      }
    }
    return whoAmI;
  }, [whoAmI]);

  const isHqUser = parsedWhoAmI?.isHqUser || false;
  const inReview = parsedInventory?.status === 'IN_REVIEW';
  const inventoryStatus = parsedInventory?.status || null;
  const total = discrepancyItems.length;
  const resolved = discrepancyItems.filter(i => i.status === 'RESOLVED' || i.status === 'OK').length;
  const issues = discrepancyItems.filter(i => i.status !== 'RESOLVED' && i.status !== 'OK').length;
  const remaining = issues;

  return {
    inventoryId,
    posId,
    items: discrepancyItems,
    total,
    issues,
    resolved,
    remaining,
    loading,
    error,
    isHqUser,
    inventoryStatus,
    inReview,
    markItemResolved,
    resolveAllItems,
    updateItemActual,
    getApiItems,
  };
};
